/**
 * Canvas + audio capture to .webm via MediaRecorder.
 *
 * For mic / tab sources we mix the source MediaStream's audio tracks into
 * the recording. For file / preanalyzed sources we use a dedicated
 * MediaStreamAudioDestinationNode the source set up.
 *
 * For absolute highest quality, recommend OBS in the README.
 */

export interface RecorderOptions {
  videoStream: MediaStream;
  audioStream?: MediaStream;
  mimeType?: string;
  videoBitsPerSecond?: number;
  audioBitsPerSecond?: number;
}

export interface Recording {
  blob: Blob;
  mimeType: string;
  /** File extension matching the actual encoded format (no leading dot). */
  extension: string;
  durationMs: number;
}

/**
 * MIME types tried in priority order. MP4 (H.264 + AAC) goes first
 * because it's natively playable in iOS Photos / Camera Roll, every
 * social-media app, and any browser; WebM is the fallback for older
 * Chromium-based browsers that don't yet support MP4 in MediaRecorder.
 *
 * Safari (iOS 14.5+, macOS Big Sur+) and Chrome 121+ both report MP4
 * support. Firefox still requires WebM as of 2026.
 */
const PREFERRED_MIMES = [
  // MP4 — H.264 video + AAC audio. Universal playback.
  "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
  "video/mp4;codecs=avc1,mp4a",
  "video/mp4",
  // WebM fallback (Firefox + older Chrome).
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
];

function pickMime(): string {
  if (typeof MediaRecorder === "undefined") return "";
  for (const m of PREFERRED_MIMES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
}

/** Map a MIME type to a sensible file extension. */
function extensionFor(mime: string): string {
  const m = mime.toLowerCase();
  if (m.startsWith("video/mp4")) return "mp4";
  if (m.startsWith("video/webm")) return "webm";
  if (m.startsWith("video/quicktime")) return "mov";
  // Conservative default — anything unknown lands as .bin so the user
  // notices and we fix it.
  return "bin";
}

export class CanvasRecorder {
  private rec: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private startedAt = 0;
  private resolveStop: ((r: Recording) => void) | null = null;
  private rejectStop: ((reason: Error) => void) | null = null;

  isRecording(): boolean {
    return this.rec !== null && this.rec.state === "recording";
  }

  start(opts: RecorderOptions): void {
    if (this.isRecording()) throw new Error("Already recording.");
    if (typeof MediaRecorder === "undefined") {
      throw new Error("MediaRecorder not supported in this browser.");
    }

    const mime = opts.mimeType ?? pickMime();
    if (!mime) {
      throw new Error("No supported MediaRecorder mime type found.");
    }

    // Combine video + audio tracks into one MediaStream for recording.
    const tracks: MediaStreamTrack[] = [...opts.videoStream.getVideoTracks()];
    if (opts.audioStream) {
      tracks.push(...opts.audioStream.getAudioTracks());
    }
    const combined = new MediaStream(tracks);

    const rec = new MediaRecorder(combined, {
      mimeType: mime,
      videoBitsPerSecond: opts.videoBitsPerSecond ?? 8_000_000,
      audioBitsPerSecond: opts.audioBitsPerSecond ?? 192_000,
    });

    this.chunks = [];
    rec.ondataavailable = (ev: BlobEvent): void => {
      if (ev.data && ev.data.size > 0) this.chunks.push(ev.data);
    };
    rec.onstop = (): void => {
      const blob = new Blob(this.chunks, { type: mime });
      this.rec = null;
      this.chunks = [];
      const resolve = this.resolveStop;
      const reject = this.rejectStop;
      this.resolveStop = null;
      this.rejectStop = null;
      if (blob.size === 0) {
        reject?.(new Error("Recorder produced an empty file. Try WebM or a lower output size."));
        return;
      }
      resolve?.({
        blob,
        mimeType: mime,
        extension: extensionFor(mime),
        durationMs: performance.now() - this.startedAt,
      });
    };
    rec.onerror = (event: ErrorEvent): void => {
      const reject = this.rejectStop;
      this.rec = null;
      this.chunks = [];
      this.resolveStop = null;
      this.rejectStop = null;
      reject?.(new Error(event.message || "MediaRecorder failed."));
    };

    this.startedAt = performance.now();
    this.rec = rec;
    rec.start(1000); // 1s chunks
  }

  /** Stops the active recording and resolves with the final blob. */
  stop(): Promise<Recording> {
    return new Promise<Recording>((resolve, reject) => {
      if (!this.rec) {
        reject(new Error("Not recording."));
        return;
      }
      this.resolveStop = resolve;
      this.rejectStop = reject;
      try {
        this.rec.stop();
      } catch (err) {
        this.resolveStop = null;
        this.rejectStop = null;
        reject(err);
      }
    });
  }
}

/** Convenience: trigger a download of the given blob. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so the download has time to start on slow browsers.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Suggest a filename like `pulsefx-2026-04-30T18-22-09.webm`. */
export function suggestFilename(ext: string): string {
  const d = new Date();
  const pad = (n: number): string => String(n).padStart(2, "0");
  const stamp =
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
  return `pulsefx-${stamp}.${ext}`;
}
